<template>
  <div class="position-picker-option" @click.stop="onSelect">
    <PositionCapacityAttachmentsBadge
      class="position-picker-option__badge"
      :class="{ 'position-picker-option__badge--disabled': disabled }"
      v-bind="badgeProps"
    />
    <OrgObjectStatusBadge
      :status="position.status || 'draft'"
      :archived="position.archived || false"
    />
    <slot name="after-status" />
    <span class="position-picker-option__text">
      <span class="position-picker-option__code">#{{ position.code || position.id }}</span>
      <span
        class="position-picker-option__name"
        :class="{ 'position-picker-option__name--disabled': disabled }"
      >{{ position.name }}</span>
    </span>
  </div>
</template>

<script setup>
import { computed } from "vue";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import PositionCapacityAttachmentsBadge from "@components/PositionCapacityAttachmentsBadge/PositionCapacityAttachmentsBadge.vue";

const props = defineProps({
  /** The position: id, code, name, status, capacity, vacancy and the attachment counts. */
  position: {
    type: Object,
    required: true,
  },
  /** Dims the badge to 40% (Figma variant Disabled=True); a disabled option cannot be chosen. */
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["select"]);

const COUNT_KEYS = [
  "validRolesCount",
  "invalidRolesCount",
  "validResponsibilitiesCount",
  "directResponsibilitiesCount",
  "notActiveDirectResponsibilitiesCount",
];

const badgeProps = computed(() => {
  const { capacity, vacancy, allocation } = props.position;
  const result = {
    capacity: typeof capacity === "number" ? capacity : 1,
    vacancy: typeof vacancy === "number" ? vacancy : null,
    allocation: typeof allocation === "string" ? allocation : "",
  };
  COUNT_KEYS.forEach((key) => {
    result[key] = typeof props.position[key] === "number" ? props.position[key] : 0;
  });
  return result;
});

function onSelect() {
  if (props.disabled) return;
  emit("select", props.position);
}
</script>

<style scoped>
.position-picker-option {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.position-picker-option__badge {
  flex-shrink: 0;
}

.position-picker-option__badge--disabled {
  opacity: 0.4;
}

.position-picker-option__text {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Inter;
  font-size: 16px;
  line-height: 24px;
}

.position-picker-option__code {
  font-weight: 400;
  color: #475467;
}

.position-picker-option__name {
  font-weight: 500;
  color: #101828;
}

.position-picker-option__name--disabled {
  color: #98a2b3;
}
</style>
